import { approvalHeading } from "../../../services/approvalTableData";
import { useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import BasicModal from "../../../components/models/models";
import Table from "../../../components/Table/table";
import Header from "../../../components/Headers/Header";
import { useDispatch, useSelector } from "react-redux";
import { useWindowSize } from "../../../shared/hooks/useWindowSize";
import {
  getApprovalRequests,
  selectPersonalDashboard,
} from "../../../redux/slices/personalDashboardSlice";
import { selectUser } from "../../../redux/slices/userSlice";
import Search from "../../../assets/images/search.png";
import { useFormattedAmount } from "../../../hooks/useFormattedAmount";

const initialState = {
  service: false,
  cost: false,
  date_needed: false,
  requester: false,
  status: false,
};

function ApprovalModule({ show, setShow }) {
  const location = useLocation();
  const stateData = location.state || {};
  const openPopup = stateData.openPopup || false;
  // const locationSetId = stateData.locationSetId || null;

  const formatFunction = useFormattedAmount();
  const [popup, setPopup] = useState(false);
  const [btns, setBtns] = useState(initialState);
  const [active, setActive] = useState("service");
  const [orderType, setOrderType] = useState("asc");
  const [statusFilter, setStatusFilter] = useState("Pending");
  const [state, setState] = useState(0);
  const [id, setId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const dispatch = useDispatch();
  const [width] = useWindowSize();
  const State = useSelector(selectPersonalDashboard);
  const { userDetails } = useSelector(selectUser);
  const { updateApprovalRequestData , approvalRequestsData} = State

  // Always ensure we have an array to prevent .map errors
  const approvalRequests = approvalRequestsData?.approval_requests || [];

  // Transform API response for table use
  const transformResponse = (response) => {
    if (!Array.isArray(response)) return [];
    return response.map((item) => ({
      id: item.subm_id,
      service: item.service,
      cost: item.cost,
      date_needed: item.date_needed,
      requester: item.requester || "",
      status: item.status,
    }));
  };

  const [currentData, setCurrentData] = useState(() =>
    transformResponse(approvalRequests)
  );

  // Update currentData whenever approvalRequests changes
  useEffect(() => {
    setCurrentData(transformResponse(approvalRequests));
  }, [approvalRequests]);

  // Fetch approvals on mount
  useEffect(() => {
    dispatch(getApprovalRequests());
  }, [dispatch, updateApprovalRequestData]);

  // Handle openPopup
  useEffect(() => {
    if (openPopup && currentData.length > 0) {
      setPopup(true);
      setState(19);
      setId({ id: currentData[0].id, userId: userDetails?.user?.id });
    } else {
      setPopup(false);
    }
  }, [openPopup, currentData, userDetails]);

  // Filter for search && status
  const displayApprovals = currentData.filter((approval) =>
    approval.service?.toLowerCase().includes(searchTerm.toLowerCase()) &&
    (statusFilter === "" || approval.status === statusFilter)
  );

  // Sorting functions (kept exactly as you requested)
  const handleServiceName = (orderType) => {
    setBtns({
      ...btns,
      service: true,
      ...(active !== "service" ? { [active]: false } : {}),
    });
    setActive("service");
    const sort = [...currentData].sort((a, b) => {
      const serviceA = a.service || "";
      const serviceB = b.service || "";
      if (orderType === "asc") return serviceB.localeCompare(serviceA);
      else return serviceA.localeCompare(serviceB);
    });
    setCurrentData(sort);
    setOrderType(orderType === "asc" ? "desc" : "asc");
  };

  const handleCost = (orderType) => {
    setBtns({
      ...btns,
      cost: true,
      ...(active !== "cost" ? { [active]: false } : {}),
    });
    setActive("cost");
    const sort = [...currentData].sort((a, b) => {
      const costA = parseFloat(a.cost) || 0;
      const costB = parseFloat(b.cost) || 0;
      if (orderType === "asc") return costB - costA;
      else return costA - costB;
    });
    setCurrentData(sort);
    setOrderType(orderType === "asc" ? "desc" : "asc");
  };

  const parseDate = (dateString) => {
    if (!dateString) return new Date(0);
    const parts = dateString.split("/");
    if (parts.length < 3) return new Date(0);

    const [day, month, year] = parts;
    const safeDay = (day || "01").padStart(2, "0");
    const safeMonth = (month || "01").padStart(2, "0");
    const safeYear = year || "1970";

    return new Date(`${safeYear}-${safeMonth}-${safeDay}`);
  };

  const handleDateNeeded = (orderType) => {
    setBtns({
      ...btns,
      date_needed: true,
      ...(active !== "date_needed" ? { [active]: false } : {}),
    });
    setActive("date_needed");
    const sort = [...currentData].sort((a, b) => {
      const dateA = parseDate(a.date_needed);
      const dateB = parseDate(b.date_needed);
      if (orderType === "asc") return dateB - dateA;
      else return dateA - dateB;
    });
    setCurrentData(sort);
    setOrderType(orderType === "asc" ? "desc" : "asc");
  };

  const handleRequester = (orderType) => {
    setBtns({
      ...btns,
      requester: true,
      ...(active !== "requester" ? { [active]: false } : {}),
    });
    setActive("requester");

    const sort = [...currentData].sort((a, b) => {
      const reqA =
        typeof a.requester === "string"
          ? a.requester
          : a.requester?.name || a.requester?.toString() || "";
      const reqB =
        typeof b.requester === "string"
          ? b.requester
          : b.requester?.name || b.requester?.toString() || "";
      if (orderType === "asc") return reqB.localeCompare(reqA);
      else return reqA.localeCompare(reqB);
    });

    setCurrentData(sort);
    setOrderType(orderType === "asc" ? "desc" : "asc");
  };

  // const handleStatus = (orderType) => {
  //   setBtns({
  //     ...btns,
  //     status: true,
  //     ...(active !== "status" ? { [active]: false } : {}),
  //   });
  //   setActive("status");
  //   const sort = [...currentData].sort((a, b) => {
  //     const statusA = a.status || "";
  //     const statusB = b.status || "";
  //     if (orderType === "asc") return statusB.localeCompare(statusA);
  //     else return statusA.localeCompare(statusB);
  //   });
  //   setCurrentData(sort);
  //   setOrderType(orderType === "asc" ? "desc" : "asc");
  // };

  return (
    <div
      onClick={() => setShow(true)}
      className="right_panel h-[100vh] md:h-[auto] flex flex-col w-[100%] gap-3 md:gap-[42px] lg:pt-1 sm:pt-[20px] xl:pt-[32px] xl:max-w-[1099px] 6xl:max-w-[80%] lg:pl-[24px] xl:pl-0 lg:pr-[24px]"
    >
      <Header
        title={width > 600 ? "Your Approvals" : "Approvals"}
        subHeading="Procurement requests awaiting your approval."
        show={show}
        setShow={setShow}
        btns={false}
      />

      <div className="searchBox pl-[12px] md:pl-0 pr-[12px] md:pr-0">
        <img src={Search} alt="icon" className="searchIcon" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search approvals"
          className="searchInput"
        />
      </div>

      <div className="card-section">
        <div className="cards_wrap bottom_space bg-[#363D50] w-[100%] md:rounded-[24px] overflow-y-scroll scrollbar-hide !h-[calc(100vh-138px)] md:!h-[calc(100vh-256px)]">
          <Table
            headings={approvalHeading}
            tableData={displayApprovals}
            setState={setState}
            setId={setId}
            setPopup={setPopup}
            table="approval"
            handleServiceName={handleServiceName}
            handleCost={handleCost}
            handleDateNeeded={handleDateNeeded}
            handleRequester={handleRequester}
            // handleStatus={handleStatus}
            orderType={orderType}
            setStatusFilter={setStatusFilter}
            statusFilter={statusFilter}
          />
        </div>
      </div>

      {popup && (
        <BasicModal
          state={state}
          id={id}
          setId={setId}
          popup={popup}
          setPopup={setPopup}
          setState={setState}
          tablename="personaldashborad"
        />
      )}
    </div>
  );
}

export default ApprovalModule;

